import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import LanguageService from '../services/LanguageService';
import { trackOnboardingCompleted } from '../services/analyticsService';
import { clearPendingAuthJourney, getPendingAuthJourney } from '../lib/authJourney';
import { saveAuthenticatedPlacementTestAttempt } from '../services/publicPlacementLeadService';
import { getPlacementResultProfileSummary } from '../services/placementAttempt';
import {
  DEFAULT_ONBOARDING_STATE,
  mergeOnboardingStateWithJourney,
  resolveOnboardingCompletionPayload,
  sanitizePlacementTestResults,
} from './onboardingHelpers';

const OnboardingContext = createContext(null);

const STORAGE_KEY = 'aprendos_onboarding_state';

export const OnboardingProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [onboardingState, setOnboardingState] = useState(DEFAULT_ONBOARDING_STATE);

  // Load onboarding status from Firestore or localStorage
  useEffect(() => {
    const loadOnboardingStatus = async () => {
      setLoading(true);

      let restoredState = DEFAULT_ONBOARDING_STATE;
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        try {
          restoredState = { ...DEFAULT_ONBOARDING_STATE, ...JSON.parse(savedState) };
        } catch {
          console.warn('Failed to parse onboarding state from localStorage');
        }
      }
      const pendingJourney = typeof window !== 'undefined'
        ? getPendingAuthJourney(window.sessionStorage)
        : null;
      restoredState = mergeOnboardingStateWithJourney(restoredState, pendingJourney);
      setOnboardingState(restoredState);

      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setOnboardingCompleted(userData.onboardingCompleted === true);

            // If mid-onboarding, restore state from localStorage
            if (!userData.onboardingCompleted) {
              setOnboardingState(restoredState);
            }
          } else {
            // New user - no document exists
            setOnboardingCompleted(false);
          }
        } catch (error) {
          console.error('Error loading onboarding status:', error);
          setOnboardingCompleted(false);
        }
      } else {
        setOnboardingCompleted(false);
      }

      setLoading(false);
    };

    loadOnboardingStatus();
  }, [currentUser]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (onboardingState.selectedLanguage || onboardingState.selectedLevel) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(onboardingState));
    }
  }, [onboardingState]);

  // Update selected language
  const setSelectedLanguage = useCallback((languageCode) => {
    setOnboardingState(prev => ({
      ...prev,
      selectedLanguage: languageCode
    }));
  }, []);

  const setOnboardingStep = useCallback((currentStep) => {
    setOnboardingState(prev => ({
      ...prev,
      currentStep,
    }));
  }, []);

  // Update selected level (from manual selection)
  const setSelectedLevel = useCallback((level) => {
    setOnboardingState(prev => ({
      ...prev,
      selectedLevel: level,
      levelSource: 'manual'
    }));
  }, []);

  // Update test results
  const setTestResults = useCallback((results) => {
    const sanitizedResults = sanitizePlacementTestResults(
      results,
      results?.selectedStartingLevel ?? results?.level,
      onboardingState.selectedLanguage
    );
    setOnboardingState(prev => ({
      ...prev,
      selectedLevel: sanitizedResults?.selectedStartingLevel || sanitizedResults?.level || null,
      levelSource: 'test',
      testResults: sanitizedResults
    }));
  }, [onboardingState.selectedLanguage]);

  // Complete onboarding and save to Firestore
  const completeOnboarding = useCallback(async (overrides = {}) => {
    if (!currentUser) {
      console.warn('Cannot complete onboarding without authenticated user');
      return false;
    }

      const {
        selectedLanguage,
        selectedLevel,
        levelSource,
        testResults,
        placementDecision,
      } = resolveOnboardingCompletionPayload(onboardingState, overrides);

    if (!selectedLanguage || !selectedLevel) {
      console.warn('Cannot complete onboarding without language and level');
      return false;
    }

    try {
      const languageService = new LanguageService(selectedLanguage);
      const existingProfile = await languageService.getUserProfileFromAppStructure(currentUser.uid).catch(() => null);
      const profileTimestamp = new Date().toISOString();
      const activeLearningLanguages = Array.from(
        new Set([
          selectedLanguage,
          ...(Array.isArray(existingProfile?.activeLearningLanguages)
            ? existingProfile.activeLearningLanguages
            : [])
        ].filter(Boolean))
      );

      let diagnosticAttemptId = testResults?.attemptId || null;
      if (testResults?.responses?.length && testResults?.testVersion) {
        try {
          const savedAttempt = await saveAuthenticatedPlacementTestAttempt({
            language: selectedLanguage,
            result: testResults,
          });
          diagnosticAttemptId = savedAttempt.attemptId || diagnosticAttemptId;
        } catch (attemptError) {
          console.error('Error saving detailed placement-test attempt:', attemptError);
        }
      }
      const placementResultSummary = getPlacementResultProfileSummary(
        testResults,
        diagnosticAttemptId
      );

      // Save to Firestore users collection
      // Phase 2: Write both learningLanguage (mobile's field) and preferredLearningLanguage (web's legacy field)
      // Phase 3: Add currentSequence for progressive unlock, matching mobile's onboarding structure
      await setDoc(doc(db, 'users', currentUser.uid), {
        email: currentUser.email,
        displayName: currentUser.displayName || null,
        onboardingCompleted: true,
        onboardingCompletedAt: serverTimestamp(),
        onboardingSource: levelSource,
        level: selectedLevel,
        learningLanguage: selectedLanguage,           // Mobile's canonical field
        preferredLearningLanguage: selectedLanguage,  // Web's legacy field for backwards compat
        activeLearningLanguages,
        currentSequence: 1,                           // For progressive unlock on mobile
        ...(placementResultSummary && { placementTestResults: { ...placementResultSummary, takenAt: serverTimestamp() } }),
        ...(placementDecision && { placementDecision: { ...placementDecision, decidedAt: serverTimestamp() } })
      }, { merge: true });

      // Seed the language-specific profile so multi-language level resolution
      // does not rely on the legacy global level field.
      await languageService.getUserProfile(currentUser.uid);
      await languageService.updateUserProfile(currentUser.uid, {
        level: selectedLevel,
        currentSequence: 1,
        ...(placementResultSummary ? { placementTestResults: { ...placementResultSummary, takenAt: profileTimestamp } } : {}),
        ...(placementDecision ? { placementDecision: { ...placementDecision, decidedAt: profileTimestamp } } : {}),
      });

      // Phase 5: Removed RTDB user progress writes
      // Mobile reads user progress from Firestore (users/{uid}), not RTDB
      // RTDB is used for content (episodes, exercises) but not user data

      // Clear localStorage
      localStorage.removeItem(STORAGE_KEY);
      if (typeof window !== 'undefined') {
        clearPendingAuthJourney(window.sessionStorage);
      }

      setOnboardingCompleted(true);
      trackOnboardingCompleted(selectedLanguage, selectedLevel, levelSource);
      return true;
    } catch (error) {
      console.error('Error completing onboarding:', error);
      return false;
    }
  }, [currentUser, onboardingState]);

  // Reset onboarding state (for testing or re-onboarding)
  const resetOnboarding = useCallback(() => {
    setOnboardingState(DEFAULT_ONBOARDING_STATE);
    localStorage.removeItem(STORAGE_KEY);
    if (typeof window !== 'undefined') {
      clearPendingAuthJourney(window.sessionStorage);
    }
    setOnboardingCompleted(false);
  }, []);

  // Clear state on logout
  const clearOnboardingState = useCallback(() => {
    setOnboardingState(DEFAULT_ONBOARDING_STATE);
    localStorage.removeItem(STORAGE_KEY);
    if (typeof window !== 'undefined') {
      clearPendingAuthJourney(window.sessionStorage);
    }
  }, []);

  const value = {
    loading,
    onboardingCompleted,
    onboardingState,
    setSelectedLanguage,
    setOnboardingStep,
    setSelectedLevel,
    setTestResults,
    completeOnboarding,
    resetOnboarding,
    clearOnboardingState
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export default OnboardingContext;
